import {
  type BasketItem,
  CONVEYANCE_TYPES,
  type DeliveryAddress,
  type Location,
  sdkUtils,
} from '@koala/sdk';
import * as Sentry from '@sentry/react';
import Router from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { ReorderForm } from './reorderForm/reorder-form';
import { ReorderConfirmConveyance } from './reorderConfirmConveyance/reorder-confirm-conveyance';
import { API_CONVEYANCE_TYPES, MODAL } from '@/constants/events';
import basketActions from '@/redux/basket/actions';
import { selectAccountSlice } from '@/redux/me';
import meActions from '@/redux/me/actions';
import { PRODUCT_LOCATION_LABELS } from '@/constants/checkout';
import { useLocation } from '@/features/locations/service';
import conveyanceModeActions, { type ConveyanceMode } from '@/redux/conveyanceMode/actions';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import { useMenu } from '@/features/menu/service';
import { API_FOR_CONVEYANCE_TYPES_SWAP } from '@/constants/global';
import globalActions from '@/redux/global/actions';
import { isBreakpointSizeMin } from '@/utils/breakpoints';
import { sessionStorageBasket, sessionStorageOptionPrompts } from '@/utils/global';
import {
  buildUnsatisfiedOptionsPrompt,
  findUnsatisfiedBasketLines,
  unsatisfiedOptionsPromptKey,
} from '@/utils/basket';
import GenericModal from '@/components/uielements/genericModal';
import { getLocationId } from '@/utils/locations';

export const ReorderModal = () => {
  const dispatch = useDispatch();
  const { reorderModalOpen, orderDetails } = useSelector(selectAccountSlice);
  const { clearMeOrderDetails, toggleReorderReceipt } = meActions;

  const [addingToCart, setAddingToCart] = useState(false);
  const [showConveyanceConfirm, setShowConveyanceConfirm] = useState(false);
  const [pendingDeliveryAddress, setPendingDeliveryAddress] = useState<DeliveryAddress | null>(
    null,
  );

  useEffect(() => {
    if (!reorderModalOpen) {
      setShowConveyanceConfirm(false);
      setPendingDeliveryAddress(null);
    }
  }, [reorderModalOpen]);

  const { time_wanted, type: conveyanceType } = useSelector(selectConveyance);
  const { categories, isLoading: menuLoading } = useMenu({
    id: orderDetails?.store_location?.id,
    wantedAt: time_wanted,
  });
  const basket = useSelector(({ app }) => app.basket.content);
  const { webConfig } = useSelector(({ app }) => app.cmsConfig);

  const conveyanceTypeKey = orderDetails?.order_data.basket.conveyance_type.type;
  const orderConveyance =
    (conveyanceTypeKey && API_FOR_CONVEYANCE_TYPES_SWAP[conveyanceTypeKey]) ||
    CONVEYANCE_TYPES.PICKUP;
  const isDeliveryOrder = orderConveyance === CONVEYANCE_TYPES.DELIVERY;
  const { data: locationDetails } = useLocation(
    isDeliveryOrder ? orderDetails?.store_location?.id : undefined,
  );

  function handleAddToCart(deliveryAddress: DeliveryAddress | null) {
    if (conveyanceType !== orderConveyance) {
      setPendingDeliveryAddress(deliveryAddress);
      setShowConveyanceConfirm(true);
      return;
    }
    addToCart(deliveryAddress);
  }

  function confirmConveyanceChange() {
    if (pendingDeliveryAddress && locationDetails?.data) {
      dispatch(
        conveyanceModeActions.setDeliveryAddress(pendingDeliveryAddress, locationDetails.data),
      );
    } else {
      const defaultConveyanceDetails: ConveyanceMode = {
        type: API_CONVEYANCE_TYPES.PICKUP,
        details: [],
      };
      const conveyanceDetails = defaultConveyanceDetails;
      dispatch(conveyanceModeActions.conveyanceModeSet(conveyanceDetails, time_wanted));
    }

    setShowConveyanceConfirm(false);
    addToCart(pendingDeliveryAddress);
  }

  function addToCart(deliveryAddress: DeliveryAddress | null) {
    const label = PRODUCT_LOCATION_LABELS.MENU;

    if (!orderDetails?.store_location || !categories.length) {
      dispatch(
        globalActions.displayErrorToast(
          "We couldn't load this restaurant's menu. Please refresh and try again.",
          true,
        ),
      );
      return;
    }

    setAddingToCart(true);

    const location: Location | undefined | { id: number } = orderDetails.store_location;
    const sessionBasket = sessionStorageBasket.get();

    const availableItems = orderDetails.order_data.basket.basket_items.filter((basketItem) =>
      sdkUtils.findMenuItemById(basketItem.product.id, categories),
    );

    if (availableItems.length === 0) {
      dispatch(
        globalActions.displayErrorToast(
          'None of the items from this order are currently available. Redirecting you to the menu.',
          true,
        ),
      );
      setAddingToCart(false);
      closeReorderModal();
      void Router.push(
        `/store/${getLocationId(location as Location, webConfig)}/${
          orderDetails.store_location.label
        }`,
      );
      return;
    }

    if (availableItems.length !== orderDetails.order_data.basket.basket_items.length) {
      dispatch(
        globalActions.displayErrorToast(
          'Some items are not currently available and have been removed from your basket.',
          false,
        ),
      );
    }

    availableItems.forEach((basketItem) => {
      const productCategory = sdkUtils.findProductCategory(basketItem.product.id, categories);
      basketItem.product.cross_sell_id = null;
      basketItem.product.category_id = productCategory?.global_id || null;
      basketItem.product.category_label = productCategory?.name || null;
    });

    // Null or mismatched location ids require an init/reinit of the basket
    // Unless the mismatch is because we are adding a cross-sell item from a basket
    // associated with a different location than the current menu we've landed on.
    // In that case we should be able to proceed adding the item to the basket normally
    Sentry.addBreadcrumb({
      category: 'Location, Basket, and Label',
      message: `Before initiating a basket and adding an item to the cart (FavoriteOrderCard)`,
      data: {
        locationId: location?.id,
        basketLocationId: basket.location.id,
        basketItemCount: basket.basket_items?.length,
        sessionBasketItemCount: sessionBasket?.content?.basket_items?.length,
        label: label,
      },
      level: 'info',
    });
    if (!basket.location.id || basket.location.id !== location?.id || !sessionBasket) {
      /**
       * If a delivery address exists, add it to localStorage when creating a basket
       *
       * @FIXME there are some issues with this action type:
       * 1. The location is nullable, which is an issue with the Location slice's empty state.
       * 2. The categories are nullable, again an issue with the Menu slice's empty state.
       * 3. The address null|undefined inconsistencies.
       */
      dispatch(
        basketActions.initBasket(
          // @ts-expect-error
          location,
          categories,
          undefined,
          deliveryAddress ?? undefined,
          time_wanted,
        ),
      );
    }

    /**
     * Add the item to the user's basket and close the modal.
     *
     * @FIXME there is an issue with this action type:
     * 1. The label is nullable, which is an issue with the Customize slice's empty state.
     */
    availableItems.forEach((basketItem) => {
      dispatch(basketActions.addItem(basketItem as BasketItem, label));
    });

    /**
     * TEAM-15138: this path reimplements `filterAvailableItems` inline (above), so it inherits the
     * same blind spot — the product still being on the menu says nothing about whether its option
     * groups still accept the diner's selection, and the rejection would land at checkout.
     *
     * Read the cart back from sessionStorage rather than indexing `availableItems`: unlike the saga
     * path, this one *appends* to whatever was already in the basket, and only reinitialises it
     * when the location changed. The ADD_ITEM reducer writes through to sessionStorage
     * synchronously, so by here the storage already holds the final array — which is the one the
     * reopen affordance indexes into.
     *
     * Signatures are recorded so `revalidateBasketItemsSaga` does not repeat this on the next
     * route change.
     */
    const cartAfterReorder = sessionStorageBasket.get()?.content?.basket_items ?? [];
    const unsatisfiedLines = findUnsatisfiedBasketLines(cartAfterReorder, categories);
    const optionsPrompt = buildUnsatisfiedOptionsPrompt(unsatisfiedLines, cartAfterReorder);
    if (optionsPrompt) {
      dispatch(
        globalActions.displayToast(
          optionsPrompt.message,
          true,
          false,
          undefined,
          optionsPrompt.reopen,
        ),
      );
    }
    // Outside the branch, matching `revalidateBasketItemsSaga`: a reorder with nothing unsatisfied
    // writes `null` and clears the previous cart's key rather than leaving it to be overwritten on
    // the destination route.
    sessionStorageOptionPrompts.set(
      unsatisfiedOptionsPromptKey(unsatisfiedLines, cartAfterReorder),
    );

    dispatch(globalActions.showReorderBanner());

    closeReorderModal();
    setAddingToCart(false);
    // don't automatically open the cart on mobile after adding an item
    if (isBreakpointSizeMin('medium')) {
      dispatch(globalActions.toggleBasket(true));
    }
  }

  const closeReorderModal = () => {
    dispatch(toggleReorderReceipt(false));
    dispatch(clearMeOrderDetails());
    setShowConveyanceConfirm(false);
    setPendingDeliveryAddress(null);
  };

  return (
    <>
      <GenericModal
        modalOpen={reorderModalOpen}
        name={MODAL.REORDER_PAST_ORDER}
        title={showConveyanceConfirm ? `Switch to ${orderConveyance}?` : 'Reorder'}
        toggleModal={() => closeReorderModal()}
        onClose={() => closeReorderModal()}
      >
        {orderDetails && !showConveyanceConfirm && (
          <ReorderForm
            order={orderDetails}
            submitting={addingToCart || menuLoading}
            onConfirm={handleAddToCart}
            onClose={closeReorderModal}
          />
        )}

        {showConveyanceConfirm && (
          <ReorderConfirmConveyance
            conveyanceType={conveyanceType}
            orderConveyance={orderConveyance}
            onConfirm={() => confirmConveyanceChange()}
            onClose={() => {
              setShowConveyanceConfirm(false);
              setPendingDeliveryAddress(null);
            }}
          />
        )}
      </GenericModal>
    </>
  );
};
